import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    OrganizationService,
    VirtualProductManagementService,
    VirtualProductConfigSchema,
    VirtualProductMode,
    NativeVirtualProductCategory,
    NativeVirtualProductVariant,
    S3Service,
    MachineService,
    MachineInfo,
    MachineVariantConfig,
} from '../api/api_pb';
import { VirtualProductCategory } from '../kiosk/v1/virtual_products_pb';
import { useClient } from '../connect_api';
import { create } from '@bufbuild/protobuf';
import CurrencyFormatter from '../utils/currencyFormatter';
import {
    Settings,
    Wifi,
    WifiOff,
    Eye,
    Save,
    Loader2,
    CheckCircle,
    ChevronDown,
    ChevronRight,
    ArrowLeft,
    Plus,
    Pencil,
    Trash2,
    X,
    Upload,
    Package,
    Monitor,
} from 'lucide-react';

const MASKED_API_KEY = '***';

// Helper to determine the effective mode from the enum value
function effectiveMode(mode: VirtualProductMode): 'external' | 'native' {
    if (mode === VirtualProductMode.NATIVE) return 'native';
    // UNSPECIFIED and EXTERNAL both map to external for backward compatibility
    return 'external';
}

// --- Category Form Modal ---
interface CategoryFormData {
    name: string;
    description: string;
    imageUrl: string;
    displayOrder: number;
    active: boolean;
}

interface CategoryFormProps {
    initial?: CategoryFormData;
    title: string;
    onSubmit: (data: CategoryFormData, imageFile: File | null) => Promise<void>;
    onCancel: () => void;
    submitting: boolean;
}

const CategoryForm: React.FC<CategoryFormProps> = ({ initial, title, onSubmit, onCancel, submitting }) => {
    const [name, setName] = useState(initial?.name ?? '');
    const [description, setDescription] = useState(initial?.description ?? '');
    const [imageUrl, setImageUrl] = useState(initial?.imageUrl ?? '');
    const [displayOrder, setDisplayOrder] = useState(initial?.displayOrder ?? 0);
    const [active, setActive] = useState(initial?.active ?? true);
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [nameError, setNameError] = useState('');

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setImageUrl(reader.result as string);
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async () => {
        if (!name.trim()) {
            setNameError('Category name is required');
            return;
        }
        setNameError('');
        await onSubmit({ name: name.trim(), description: description.trim(), imageUrl, displayOrder, active }, imageFile);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="category-modal">
            <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-lg mx-4">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                    <button onClick={onCancel} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => { setName(e.target.value); if (nameError) setNameError(''); }}
                            placeholder="e.g., Green Fees"
                            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${nameError ? 'border-red-500' : 'border-gray-300'}`}
                            data-testid="category-name-input"
                        />
                        {nameError && <p className="mt-1 text-sm text-red-600">{nameError}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Optional details about this category"
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            data-testid="category-description-input"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
                        <div className="flex items-center gap-3">
                            <label className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-md cursor-pointer transition-colors">
                                <Upload className="w-4 h-4 text-gray-600" />
                                <span className="text-sm text-gray-700">Choose Image</span>
                                <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" data-testid="category-image-input" />
                            </label>
                            {imageUrl && (
                                <img src={imageUrl} alt="Preview" className="w-10 h-10 rounded-lg object-cover" />
                            )}
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                        <input
                            type="number"
                            value={displayOrder}
                            onChange={(e) => setDisplayOrder(parseInt(e.target.value, 10) || 0)}
                            className="w-24 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            data-testid="category-order-input"
                        />
                        <p className="mt-1 text-xs text-gray-500">Lower values appear first</p>
                    </div>

                    {initial && (
                        <div>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={active}
                                    onChange={(e) => setActive(e.target.checked)}
                                    className="form-checkbox h-4 w-4 text-indigo-600"
                                    data-testid="category-active-checkbox"
                                />
                                <span className="text-sm text-gray-700">Active (visible on kiosks)</span>
                            </label>
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-3 mt-6">
                    <button onClick={onCancel} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors">
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50"
                        data-testid="category-submit-btn"
                    >
                        {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                        {initial ? 'Update' : 'Create'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- Variant Form Modal ---
interface VariantFormData {
    sku: string;
    name: string;
    priceInCents: number;
    description: string;
    displayOrder: number;
    active: boolean;
}

interface VariantFormProps {
    initial?: VariantFormData;
    title: string;
    onSubmit: (data: VariantFormData) => Promise<void>;
    onCancel: () => void;
    submitting: boolean;
}

const VariantForm: React.FC<VariantFormProps> = ({ initial, title, onSubmit, onCancel, submitting }) => {
    const [sku, setSku] = useState(initial?.sku ?? '');
    const [name, setName] = useState(initial?.name ?? '');
    const [priceStr, setPriceStr] = useState(initial ? (initial.priceInCents / 100).toFixed(2) : '');
    const [description, setDescription] = useState(initial?.description ?? '');
    const [displayOrder, setDisplayOrder] = useState(initial?.displayOrder ?? 0);
    const [active, setActive] = useState(initial?.active ?? true);
    const [nameError, setNameError] = useState('');
    const [priceError, setPriceError] = useState('');

    const handleSubmit = async () => {
        let hasError = false;
        if (!name.trim()) {
            setNameError('Variant name is required');
            hasError = true;
        } else {
            setNameError('');
        }

        const priceInCents = Math.round(parseFloat(priceStr) * 100);
        if (!priceStr || isNaN(priceInCents) || priceInCents <= 0) {
            setPriceError('Price must be greater than $0.00');
            hasError = true;
        } else {
            setPriceError('');
        }

        if (hasError) return;

        await onSubmit({
            sku: sku.trim(),
            name: name.trim(),
            priceInCents,
            description: description.trim(),
            displayOrder,
            active,
        });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="variant-modal">
            <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-lg mx-4">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                    <button onClick={onCancel} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
                        <input
                            type="text"
                            value={sku}
                            onChange={(e) => setSku(e.target.value)}
                            placeholder="Auto-generated if empty"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            data-testid="variant-sku-input"
                            disabled={!!initial}
                        />
                        {!initial && <p className="mt-1 text-xs text-gray-500">Leave blank to auto-generate</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => { setName(e.target.value); if (nameError) setNameError(''); }}
                            placeholder="e.g., 18-Hole Weekday"
                            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${nameError ? 'border-red-500' : 'border-gray-300'}`}
                            data-testid="variant-name-input"
                        />
                        {nameError && <p className="mt-1 text-sm text-red-600">{nameError}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Price *</label>
                        <div className="flex items-center">
                            <span className="mr-2 text-gray-600">$</span>
                            <input
                                type="text"
                                value={priceStr}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/[^0-9.]/g, '');
                                    setPriceStr(val);
                                    if (priceError) setPriceError('');
                                }}
                                placeholder="0.00"
                                className={`w-32 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${priceError ? 'border-red-500' : 'border-gray-300'}`}
                                data-testid="variant-price-input"
                            />
                        </div>
                        {priceError && <p className="mt-1 text-sm text-red-600">{priceError}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Optional details"
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            data-testid="variant-description-input"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                        <input
                            type="number"
                            value={displayOrder}
                            onChange={(e) => setDisplayOrder(parseInt(e.target.value, 10) || 0)}
                            className="w-24 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            data-testid="variant-order-input"
                        />
                    </div>

                    {initial && (
                        <div>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={active}
                                    onChange={(e) => setActive(e.target.checked)}
                                    className="form-checkbox h-4 w-4 text-indigo-600"
                                    data-testid="variant-active-checkbox"
                                />
                                <span className="text-sm text-gray-700">Active (visible on kiosks)</span>
                            </label>
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-3 mt-6">
                    <button onClick={onCancel} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors">
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50"
                        data-testid="variant-submit-btn"
                    >
                        {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                        {initial ? 'Update' : 'Create'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- Confirmation Dialog ---
interface ConfirmDialogProps {
    title: string;
    message: string;
    onConfirm: () => void;
    onCancel: () => void;
    confirming: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({ title, message, onConfirm, onCancel, confirming }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="confirm-dialog">
        <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-sm mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
            <p className="text-sm text-gray-600 mb-6">{message}</p>
            <div className="flex justify-end gap-3">
                <button onClick={onCancel} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors">
                    Cancel
                </button>
                <button
                    onClick={onConfirm}
                    disabled={confirming}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
                    data-testid="confirm-delete-btn"
                >
                    {confirming && <Loader2 className="w-4 h-4 animate-spin" />}
                    Delete
                </button>
            </div>
        </div>
    </div>
);

// --- Main Component ---
const VirtualProducts: React.FC = () => {
    const navigate = useNavigate();
    const organizationClient = useClient(OrganizationService);
    const virtualProductClient = useClient(VirtualProductManagementService);
    const s3Client = useClient(S3Service);

    // Loading / saving states
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [previewing, setPreviewing] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Config state
    const [enabled, setEnabled] = useState(false);
    const [mode, setMode] = useState<'external' | 'native'>('external');
    const [apiUrl, setApiUrl] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [apiKeyChanged, setApiKeyChanged] = useState(false);

    // External preview state
    const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
    const [previewCategories, setPreviewCategories] = useState<VirtualProductCategory[]>([]);
    const [expandedPreviewCategories, setExpandedPreviewCategories] = useState<Set<string>>(new Set());
    const [showPreview, setShowPreview] = useState(false);

    // Native product state
    const [nativeCategories, setNativeCategories] = useState<NativeVirtualProductCategory[]>([]);
    const [expandedNativeCategories, setExpandedNativeCategories] = useState<Set<string>>(new Set());
    const [loadingCategories, setLoadingCategories] = useState(false);

    // Modal state
    const [categoryModal, setCategoryModal] = useState<{ type: 'create' | 'edit'; category?: NativeVirtualProductCategory } | null>(null);
    const [variantModal, setVariantModal] = useState<{ type: 'create' | 'edit'; categoryUuid: string; variant?: NativeVirtualProductVariant } | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<{ type: 'category' | 'variant'; uuid: string; name: string } | null>(null);

    const [urlError, setUrlError] = useState('');

    // Per-machine variant config state
    const machineClient = useClient(MachineService);
    const [machines, setMachines] = useState<MachineInfo[]>([]);
    const [selectedMachineUuid, setSelectedMachineUuid] = useState('');
    const [machineConfigs, setMachineConfigs] = useState<MachineVariantConfig[]>([]);
    const [enabledVariantUuids, setEnabledVariantUuids] = useState<Set<string>>(new Set());
    const [loadingMachines, setLoadingMachines] = useState(false);
    const [loadingConfigs, setLoadingConfigs] = useState(false);
    const [savingConfigs, setSavingConfigs] = useState(false);

    // --- Fetch configuration ---
    const fetchConfig = useCallback(async () => {
        try {
            const response = await organizationClient.getMyOrganizationInfo({});
            const orgInfo = response.organizationInfo;
            if (orgInfo?.virtualProductConfig) {
                const config = orgInfo.virtualProductConfig;
                setEnabled(config.enabled);
                setMode(effectiveMode(config.mode));
                setApiUrl(config.externalApiUrl);
                setApiKey('');
                setApiKeyChanged(false);
            }
        } catch (error) {
            console.error('Failed to fetch virtual product config:', error);
            toast.error('Failed to load virtual product configuration.');
        } finally {
            setLoading(false);
        }
    }, [organizationClient]);

    // --- Fetch native categories ---
    const fetchNativeCategories = useCallback(async () => {
        setLoadingCategories(true);
        try {
            const response = await virtualProductClient.listNativeCategories({});
            setNativeCategories(response.categories);
        } catch (error) {
            console.error('Failed to fetch native categories:', error);
            toast.error('Failed to load native product categories.');
        } finally {
            setLoadingCategories(false);
        }
    }, [virtualProductClient]);

    useEffect(() => {
        fetchConfig();
    }, [fetchConfig]);

    // Load native categories when mode is native and page loads
    useEffect(() => {
        if (!loading && enabled && mode === 'native') {
            fetchNativeCategories();
        }
    }, [loading, enabled, mode, fetchNativeCategories]);

    // --- Fetch machines for per-machine config ---
    const fetchMachines = useCallback(async () => {
        setLoadingMachines(true);
        try {
            const response = await machineClient.listMachines({});
            setMachines(response.machines);
        } catch (error) {
            console.error('Failed to fetch machines:', error);
            toast.error('Failed to load machines.');
        } finally {
            setLoadingMachines(false);
        }
    }, [machineClient]);

    // Load machines when mode is native
    useEffect(() => {
        if (!loading && enabled && mode === 'native') {
            fetchMachines();
        }
    }, [loading, enabled, mode, fetchMachines]);

    // --- Fetch machine variant configs ---
    const fetchMachineVariantConfigs = useCallback(async (machineUuid: string) => {
        setLoadingConfigs(true);
        try {
            const response = await virtualProductClient.getMachineVariantConfigs({ machineUuid });
            setMachineConfigs(response.configs);
            setEnabledVariantUuids(new Set(
                response.configs.filter(c => c.enabled).map(c => c.variantUuid)
            ));
        } catch (error) {
            console.error('Failed to fetch machine variant configs:', error);
            toast.error('Failed to load machine variant configuration.');
        } finally {
            setLoadingConfigs(false);
        }
    }, [virtualProductClient]);

    // Fetch configs when a machine is selected
    useEffect(() => {
        if (selectedMachineUuid) {
            fetchMachineVariantConfigs(selectedMachineUuid);
        } else {
            setMachineConfigs([]);
            setEnabledVariantUuids(new Set());
        }
    }, [selectedMachineUuid, fetchMachineVariantConfigs]);

    // --- Save machine variant configs ---
    const handleSaveMachineConfigs = async () => {
        if (!selectedMachineUuid) return;
        setSavingConfigs(true);
        try {
            await virtualProductClient.updateMachineVariantConfigs({
                machineUuid: selectedMachineUuid,
                enabledVariantUuids: Array.from(enabledVariantUuids),
            });
            toast.dismiss();
            toast.success('Machine variant configuration saved successfully');
            // Refresh to get the latest state
            await fetchMachineVariantConfigs(selectedMachineUuid);
        } catch (error) {
            console.error('Failed to save machine variant configs:', error);
            toast.dismiss();
            toast.error('Failed to save machine configuration: ' + (error as Error).message);
        } finally {
            setSavingConfigs(false);
        }
    };

    const toggleVariantEnabled = (variantUuid: string) => {
        setEnabledVariantUuids(prev => {
            const next = new Set(prev);
            if (next.has(variantUuid)) {
                next.delete(variantUuid);
            } else {
                next.add(variantUuid);
            }
            return next;
        });
    };

    // --- URL validation (external mode) ---
    const validateUrl = (url: string): boolean => {
        if (!url.trim()) {
            setUrlError('API URL is required when virtual products are enabled');
            return false;
        }
        try {
            const parsed = new URL(url);
            if (!['http:', 'https:'].includes(parsed.protocol)) {
                setUrlError('URL must use http or https protocol');
                return false;
            }
            setUrlError('');
            return true;
        } catch {
            setUrlError('Invalid URL format');
            return false;
        }
    };

    // --- Save configuration ---
    const handleSave = async () => {
        if (enabled && mode === 'external') {
            if (!validateUrl(apiUrl)) return;
        }

        setSaving(true);
        try {
            const currentResponse = await organizationClient.getMyOrganizationInfo({});
            const currentOrgInfo = currentResponse.organizationInfo;
            if (!currentOrgInfo) {
                toast.error('Failed to load current organization info');
                return;
            }

            const config = create(VirtualProductConfigSchema, {
                enabled,
                externalApiUrl: mode === 'external' ? apiUrl.trim() : '',
                externalApiKey: mode === 'external' && apiKeyChanged ? apiKey : '',
                mode: mode === 'native' ? VirtualProductMode.NATIVE : VirtualProductMode.EXTERNAL,
            });

            await organizationClient.updateOrganizationInfo({
                organizationInfo: {
                    ...currentOrgInfo,
                    virtualProductConfig: config,
                },
            });

            toast.dismiss();
            toast.success('Virtual product configuration saved successfully');
            setApiKeyChanged(false);
            await fetchConfig();

            // If switching to native mode, load categories
            if (mode === 'native') {
                await fetchNativeCategories();
            }
        } catch (error) {
            console.error('Failed to save virtual product config:', error);
            toast.dismiss();
            toast.error('Failed to save configuration: ' + (error as Error).message);
        } finally {
            setSaving(false);
        }
    };

    // --- External: Test connection ---
    const handleTest = async () => {
        if (!validateUrl(apiUrl)) return;
        setTesting(true);
        setTestResult(null);
        try {
            const response = await virtualProductClient.testVirtualProductConfig({
                externalApiUrl: apiUrl.trim(),
                externalApiKey: apiKeyChanged ? apiKey : '',
            });
            setTestResult({
                success: response.success,
                message: response.success ? 'Connection successful' : response.errorMessage || 'Connection failed',
            });
        } catch (error) {
            console.error('Failed to test virtual product config:', error);
            setTestResult({ success: false, message: 'Failed to test: ' + (error as Error).message });
        } finally {
            setTesting(false);
        }
    };

    // --- External: Preview products ---
    const handlePreview = async () => {
        setPreviewing(true);
        setShowPreview(false);
        try {
            const response = await virtualProductClient.previewVirtualProducts({});
            setPreviewCategories(response.categories);
            setShowPreview(true);
            if (response.categories.length === 0) {
                toast.info('No virtual products found from the external API');
            }
            setExpandedPreviewCategories(new Set(response.categories.map(c => c.name)));
        } catch (error) {
            console.error('Failed to preview virtual products:', error);
            toast.dismiss();
            toast.error('Failed to preview products: ' + (error as Error).message);
        } finally {
            setPreviewing(false);
        }
    };

    const togglePreviewCategory = (name: string) => {
        setExpandedPreviewCategories(prev => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name);
            else next.add(name);
            return next;
        });
    };

    // --- Native: Upload image to S3 ---
    const uploadImageToS3 = async (file: File): Promise<string> => {
        const extension = file.name.split('.').pop() || 'jpg';
        const { url } = await s3Client.getUploadUrl({ extension });
        const uploadResponse = await fetch(url, {
            method: 'PUT',
            body: file,
            headers: { 'Content-Type': file.type },
        });
        if (!uploadResponse.ok) {
            throw new Error('Failed to upload image');
        }
        return url.split('?')[0];
    };

    // --- Native: Category CRUD ---
    const handleCreateCategory = async (data: CategoryFormData, imageFile: File | null) => {
        setSubmitting(true);
        try {
            let imageUrl = '';
            if (imageFile) {
                imageUrl = await uploadImageToS3(imageFile);
            }
            await virtualProductClient.createNativeCategory({
                name: data.name,
                description: data.description,
                imageUrl,
                displayOrder: data.displayOrder,
            });
            toast.dismiss();
            toast.success('Category created successfully');
            setCategoryModal(null);
            await fetchNativeCategories();
        } catch (error) {
            console.error('Failed to create category:', error);
            toast.dismiss();
            toast.error('Failed to create category: ' + (error as Error).message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdateCategory = async (categoryUuid: string, data: CategoryFormData, imageFile: File | null) => {
        setSubmitting(true);
        try {
            let imageUrl = data.imageUrl;
            if (imageFile) {
                imageUrl = await uploadImageToS3(imageFile);
            }
            // For editing, if the imageUrl is a data URL (preview), use the original
            if (imageUrl.startsWith('data:')) {
                const originalCat = nativeCategories.find(c => c.uuid === categoryUuid);
                imageUrl = originalCat?.imageUrl ?? '';
            }
            await virtualProductClient.updateNativeCategory({
                categoryUuid,
                name: data.name,
                description: data.description,
                imageUrl,
                displayOrder: data.displayOrder,
                active: data.active,
            });
            toast.dismiss();
            toast.success('Category updated successfully');
            setCategoryModal(null);
            await fetchNativeCategories();
        } catch (error) {
            console.error('Failed to update category:', error);
            toast.dismiss();
            toast.error('Failed to update category: ' + (error as Error).message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteCategory = async (categoryUuid: string) => {
        setSubmitting(true);
        try {
            await virtualProductClient.deleteNativeCategory({ categoryUuid });
            toast.dismiss();
            toast.success('Category deleted successfully');
            setDeleteConfirm(null);
            await fetchNativeCategories();
        } catch (error) {
            console.error('Failed to delete category:', error);
            toast.dismiss();
            toast.error('Failed to delete category: ' + (error as Error).message);
        } finally {
            setSubmitting(false);
        }
    };

    // --- Native: Variant CRUD ---
    const handleCreateVariant = async (categoryUuid: string, data: VariantFormData) => {
        setSubmitting(true);
        try {
            await virtualProductClient.createNativeVariant({
                categoryUuid,
                name: data.name,
                priceInCents: data.priceInCents,
                description: data.description,
                displayOrder: data.displayOrder,
                sku: data.sku,
            });
            toast.dismiss();
            toast.success('Variant created successfully');
            setVariantModal(null);
            await fetchNativeCategories();
        } catch (error) {
            console.error('Failed to create variant:', error);
            toast.dismiss();
            toast.error('Failed to create variant: ' + (error as Error).message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdateVariant = async (variantUuid: string, data: VariantFormData) => {
        setSubmitting(true);
        try {
            await virtualProductClient.updateNativeVariant({
                variantUuid,
                name: data.name,
                priceInCents: data.priceInCents,
                description: data.description,
                displayOrder: data.displayOrder,
                active: data.active,
            });
            toast.dismiss();
            toast.success('Variant updated successfully');
            setVariantModal(null);
            await fetchNativeCategories();
        } catch (error) {
            console.error('Failed to update variant:', error);
            toast.dismiss();
            toast.error('Failed to update variant: ' + (error as Error).message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteVariant = async (variantUuid: string) => {
        setSubmitting(true);
        try {
            await virtualProductClient.deleteNativeVariant({ variantUuid });
            toast.dismiss();
            toast.success('Variant deleted successfully');
            setDeleteConfirm(null);
            await fetchNativeCategories();
        } catch (error) {
            console.error('Failed to delete variant:', error);
            toast.dismiss();
            toast.error('Failed to delete variant: ' + (error as Error).message);
        } finally {
            setSubmitting(false);
        }
    };

    const toggleNativeCategory = (uuid: string) => {
        setExpandedNativeCategories(prev => {
            const next = new Set(prev);
            if (next.has(uuid)) next.delete(uuid);
            else next.add(uuid);
            return next;
        });
    };

    // --- Render ---
    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-2" />
                    <p className="text-gray-600">Loading configuration...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-4xl animate-fade-in">
            {/* Header */}
            <div className="mb-8">
                <button
                    onClick={() => navigate('/edit-organization-info')}
                    className="flex items-center gap-1 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                    <span className="text-sm">Back to Organization Settings</span>
                </button>
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                        <Settings className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Virtual Products</h1>
                        <p className="text-gray-600 text-sm">
                            Configure virtual products for kiosks -- either from an external API or managed natively
                        </p>
                    </div>
                </div>
            </div>

            {/* Enable/Disable Toggle */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
                <label className="flex items-center justify-between cursor-pointer">
                    <div>
                        <span className="text-gray-900 font-medium">Enable Virtual Products</span>
                        <p className="text-sm text-gray-500 mt-1">
                            When enabled, virtual products will be available on kiosks
                        </p>
                    </div>
                    <div className="relative">
                        <input
                            type="checkbox"
                            checked={enabled}
                            onChange={(e) => setEnabled(e.target.checked)}
                            className="sr-only"
                        />
                        <div className={`w-11 h-6 rounded-full transition-colors ${enabled ? 'bg-indigo-600' : 'bg-gray-300'}`}>
                            <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform mt-0.5 ${enabled ? 'translate-x-5.5 ml-[22px]' : 'translate-x-0.5 ml-[2px]'}`} />
                        </div>
                    </div>
                </label>
            </div>

            {/* Mode Selector - shown when enabled */}
            {enabled && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Product Source</h3>
                    <p className="text-sm text-gray-500 mb-4">Choose how virtual products are sourced for your kiosks</p>
                    <div className="flex gap-4">
                        <label
                            className={`flex-1 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                                mode === 'external'
                                    ? 'border-indigo-500 bg-indigo-50'
                                    : 'border-gray-200 hover:border-gray-300'
                            }`}
                            data-testid="mode-external"
                        >
                            <input
                                type="radio"
                                name="productMode"
                                value="external"
                                checked={mode === 'external'}
                                onChange={() => setMode('external')}
                                className="sr-only"
                            />
                            <div className="flex items-center gap-2 mb-1">
                                <Wifi className="w-4 h-4 text-indigo-600" />
                                <span className="font-medium text-gray-900">External API</span>
                            </div>
                            <p className="text-xs text-gray-500">Fetch products from a third-party API</p>
                        </label>

                        <label
                            className={`flex-1 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                                mode === 'native'
                                    ? 'border-indigo-500 bg-indigo-50'
                                    : 'border-gray-200 hover:border-gray-300'
                            }`}
                            data-testid="mode-native"
                        >
                            <input
                                type="radio"
                                name="productMode"
                                value="native"
                                checked={mode === 'native'}
                                onChange={() => setMode('native')}
                                className="sr-only"
                            />
                            <div className="flex items-center gap-2 mb-1">
                                <Package className="w-4 h-4 text-indigo-600" />
                                <span className="font-medium text-gray-900">Native Products</span>
                            </div>
                            <p className="text-xs text-gray-500">Manage products directly in this portal</p>
                        </label>
                    </div>
                </div>
            )}

            {/* ===== EXTERNAL MODE ===== */}
            {enabled && mode === 'external' && (
                <>
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">API Configuration</h3>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">External API URL</label>
                            <input
                                type="url"
                                value={apiUrl}
                                onChange={(e) => { setApiUrl(e.target.value); if (urlError) validateUrl(e.target.value); }}
                                onBlur={() => apiUrl && validateUrl(apiUrl)}
                                placeholder="https://api.example.com/v1"
                                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${urlError ? 'border-red-500' : 'border-gray-300'}`}
                            />
                            {urlError && <p className="mt-1 text-sm text-red-600">{urlError}</p>}
                        </div>

                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
                            <input
                                type="password"
                                value={apiKeyChanged ? apiKey : MASKED_API_KEY}
                                onChange={(e) => { setApiKey(e.target.value); setApiKeyChanged(true); }}
                                onFocus={() => { if (!apiKeyChanged) { setApiKey(''); setApiKeyChanged(true); } }}
                                placeholder="Enter API key"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                                API key is stored securely. Only enter a new key if you want to change it.
                            </p>
                        </div>

                        <div className="flex flex-wrap gap-3">
                            <button
                                onClick={handleTest}
                                disabled={testing || !apiUrl.trim()}
                                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wifi className="w-4 h-4" />}
                                Verify Connection
                            </button>

                            <button
                                onClick={handlePreview}
                                disabled={previewing}
                                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                                Preview Products
                            </button>
                        </div>

                        {testResult && (
                            <div className={`mt-4 p-3 rounded-lg flex items-center gap-2 ${
                                testResult.success
                                    ? 'bg-green-50 border border-green-200 text-green-800'
                                    : 'bg-red-50 border border-red-200 text-red-800'
                            }`}>
                                {testResult.success ? (
                                    <CheckCircle className="w-5 h-5 flex-shrink-0" />
                                ) : (
                                    <WifiOff className="w-5 h-5 flex-shrink-0" />
                                )}
                                <span className="text-sm">{testResult.message}</span>
                            </div>
                        )}
                    </div>

                    {/* Preview Section */}
                    {showPreview && previewCategories.length > 0 && (
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                Product Preview ({previewCategories.length} {previewCategories.length === 1 ? 'category' : 'categories'})
                            </h3>
                            <div className="space-y-3">
                                {previewCategories.map((category) => (
                                    <div key={category.name} className="border border-gray-200 rounded-lg overflow-hidden">
                                        <button
                                            onClick={() => togglePreviewCategory(category.name)}
                                            className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                                        >
                                            <div className="flex items-center gap-3">
                                                {category.imageUrl && (
                                                    <img src={category.imageUrl} alt={category.name} className="w-10 h-10 rounded-lg object-cover"
                                                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                                )}
                                                <div>
                                                    <p className="font-medium text-gray-900">{category.name}</p>
                                                    {category.description && <p className="text-sm text-gray-500">{category.description}</p>}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm text-gray-500">
                                                    {category.variants.length} {category.variants.length === 1 ? 'variant' : 'variants'}
                                                </span>
                                                {expandedPreviewCategories.has(category.name) ? (
                                                    <ChevronDown className="w-4 h-4 text-gray-400" />
                                                ) : (
                                                    <ChevronRight className="w-4 h-4 text-gray-400" />
                                                )}
                                            </div>
                                        </button>
                                        {expandedPreviewCategories.has(category.name) && category.variants.length > 0 && (
                                            <div className="border-t border-gray-200">
                                                <table className="w-full">
                                                    <thead>
                                                        <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            <th className="px-4 py-2">SKU</th>
                                                            <th className="px-4 py-2">Name</th>
                                                            <th className="px-4 py-2">Description</th>
                                                            <th className="px-4 py-2 text-right">Price</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-100">
                                                        {category.variants.map((variant) => (
                                                            <tr key={variant.sku} className="hover:bg-gray-50">
                                                                <td className="px-4 py-2 text-sm font-mono text-gray-600">{variant.sku}</td>
                                                                <td className="px-4 py-2 text-sm font-medium text-gray-900">{variant.name}</td>
                                                                <td className="px-4 py-2 text-sm text-gray-500">{variant.description || '-'}</td>
                                                                <td className="px-4 py-2 text-sm text-right font-medium">
                                                                    <CurrencyFormatter priceInCents={variant.priceInCents} />
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* ===== NATIVE MODE ===== */}
            {enabled && mode === 'native' && (
                <>
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Product Categories</h3>
                        <button
                            onClick={() => setCategoryModal({ type: 'create' })}
                            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors text-sm"
                            data-testid="add-category-btn"
                        >
                            <Plus className="w-4 h-4" />
                            Add Category
                        </button>
                    </div>

                    {loadingCategories ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                        </div>
                    ) : nativeCategories.length === 0 ? (
                        <div className="text-center py-12 text-gray-500" data-testid="no-categories">
                            <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                            <p className="font-medium">No categories yet</p>
                            <p className="text-sm mt-1">Create your first product category to get started</p>
                        </div>
                    ) : (
                        <div className="space-y-3" data-testid="native-categories-list">
                            {nativeCategories.map((category) => (
                                <div key={category.uuid} className="border border-gray-200 rounded-lg overflow-hidden">
                                    {/* Category Header */}
                                    <div className="flex items-center justify-between p-4 bg-gray-50">
                                        <button
                                            onClick={() => toggleNativeCategory(category.uuid)}
                                            className="flex items-center gap-3 flex-1 text-left"
                                        >
                                            {category.imageUrl && (
                                                <img src={category.imageUrl} alt={category.name} className="w-10 h-10 rounded-lg object-cover"
                                                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                            )}
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2">
                                                    <p className="font-medium text-gray-900">{category.name}</p>
                                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
                                                        category.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                                                    }`}>
                                                        {category.active ? 'Active' : 'Inactive'}
                                                    </span>
                                                </div>
                                                {category.description && <p className="text-sm text-gray-500">{category.description}</p>}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm text-gray-500">
                                                    {category.variants.length} {category.variants.length === 1 ? 'variant' : 'variants'}
                                                </span>
                                                {expandedNativeCategories.has(category.uuid) ? (
                                                    <ChevronDown className="w-4 h-4 text-gray-400" />
                                                ) : (
                                                    <ChevronRight className="w-4 h-4 text-gray-400" />
                                                )}
                                            </div>
                                        </button>
                                        <div className="flex items-center gap-1 ml-3">
                                            <button
                                                onClick={() => setCategoryModal({ type: 'edit', category })}
                                                className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                                                title="Edit category"
                                                data-testid={`edit-category-${category.uuid}`}
                                            >
                                                <Pencil className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => setDeleteConfirm({ type: 'category', uuid: category.uuid, name: category.name })}
                                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                                title="Delete category"
                                                data-testid={`delete-category-${category.uuid}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Variants Section */}
                                    {expandedNativeCategories.has(category.uuid) && (
                                        <div className="border-t border-gray-200">
                                            {category.variants.length > 0 ? (
                                                <table className="w-full">
                                                    <thead>
                                                        <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            <th className="px-4 py-2">SKU</th>
                                                            <th className="px-4 py-2">Name</th>
                                                            <th className="px-4 py-2">Description</th>
                                                            <th className="px-4 py-2 text-right">Price</th>
                                                            <th className="px-4 py-2 text-center">Status</th>
                                                            <th className="px-4 py-2 text-right">Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-100">
                                                        {category.variants.map((variant) => (
                                                            <tr key={variant.uuid} className="hover:bg-gray-50">
                                                                <td className="px-4 py-2 text-sm font-mono text-gray-600">{variant.sku}</td>
                                                                <td className="px-4 py-2 text-sm font-medium text-gray-900">{variant.name}</td>
                                                                <td className="px-4 py-2 text-sm text-gray-500">{variant.description || '-'}</td>
                                                                <td className="px-4 py-2 text-sm text-right font-medium">
                                                                    <CurrencyFormatter priceInCents={variant.priceInCents} />
                                                                </td>
                                                                <td className="px-4 py-2 text-center">
                                                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
                                                                        variant.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                                                                    }`}>
                                                                        {variant.active ? 'Active' : 'Inactive'}
                                                                    </span>
                                                                </td>
                                                                <td className="px-4 py-2 text-right">
                                                                    <div className="flex items-center justify-end gap-1">
                                                                        <button
                                                                            onClick={() => setVariantModal({ type: 'edit', categoryUuid: category.uuid, variant })}
                                                                            className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                                                                            title="Edit variant"
                                                                            data-testid={`edit-variant-${variant.uuid}`}
                                                                        >
                                                                            <Pencil className="w-3.5 h-3.5" />
                                                                        </button>
                                                                        <button
                                                                            onClick={() => setDeleteConfirm({ type: 'variant', uuid: variant.uuid, name: variant.name })}
                                                                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                                                            title="Delete variant"
                                                                            data-testid={`delete-variant-${variant.uuid}`}
                                                                        >
                                                                            <Trash2 className="w-3.5 h-3.5" />
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            ) : (
                                                <p className="px-4 py-3 text-sm text-gray-500">No variants yet</p>
                                            )}
                                            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                                                <button
                                                    onClick={() => setVariantModal({ type: 'create', categoryUuid: category.uuid })}
                                                    className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800 transition-colors"
                                                    data-testid={`add-variant-${category.uuid}`}
                                                >
                                                    <Plus className="w-3.5 h-3.5" />
                                                    Add Variant
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* ===== PER-MACHINE CONFIGURATION ===== */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6" data-testid="machine-config-section">
                    <div className="flex items-center gap-3 mb-4">
                        <Monitor className="w-5 h-5 text-indigo-600" />
                        <h3 className="text-lg font-semibold text-gray-900">Per-Machine Configuration</h3>
                    </div>
                    <p className="text-sm text-gray-500 mb-4">
                        Select a machine to configure which product variants are available on its kiosk.
                    </p>

                    {/* Machine Selector */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Machine</label>
                        {loadingMachines ? (
                            <div className="flex items-center gap-2 py-2">
                                <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
                                <span className="text-sm text-gray-500">Loading machines...</span>
                            </div>
                        ) : (
                            <select
                                value={selectedMachineUuid}
                                onChange={(e) => setSelectedMachineUuid(e.target.value)}
                                className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                data-testid="machine-selector"
                            >
                                <option value="">-- Select a machine --</option>
                                {machines.map((machine) => (
                                    <option key={machine.uuid} value={machine.uuid}>
                                        {machine.name}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>

                    {/* Variant Checklist */}
                    {selectedMachineUuid && (
                        <>
                            {loadingConfigs ? (
                                <div className="flex items-center justify-center py-8">
                                    <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                                </div>
                            ) : machineConfigs.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    <Package className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                                    <p className="text-sm">No variants available. Create product categories and variants first.</p>
                                </div>
                            ) : (
                                <>
                                    <div className="space-y-4">
                                        {Object.entries(
                                            machineConfigs.reduce<Record<string, MachineVariantConfig[]>>((acc, config) => {
                                                const category = config.categoryName || 'Uncategorized';
                                                if (!acc[category]) acc[category] = [];
                                                acc[category].push(config);
                                                return acc;
                                            }, {})
                                        ).map(([categoryName, variants]) => (
                                            <div key={categoryName} className="border border-gray-200 rounded-lg overflow-hidden">
                                                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                                    <h4 className="text-sm font-semibold text-gray-700">{categoryName}</h4>
                                                </div>
                                                <div className="divide-y divide-gray-100">
                                                    {variants.map((config) => (
                                                        <label
                                                            key={config.variantUuid}
                                                            className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={enabledVariantUuids.has(config.variantUuid)}
                                                                    onChange={() => toggleVariantEnabled(config.variantUuid)}
                                                                    className="form-checkbox h-4 w-4 text-indigo-600 rounded"
                                                                    data-testid={`variant-toggle-${config.variantUuid}`}
                                                                />
                                                                <div>
                                                                    <p className="text-sm font-medium text-gray-900">{config.variantName}</p>
                                                                    {config.variantSku && (
                                                                        <p className="text-xs text-gray-500 font-mono">{config.variantSku}</p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <span className="text-sm font-medium text-gray-700">
                                                                <CurrencyFormatter priceInCents={config.priceInCents} />
                                                            </span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="mt-4 flex items-center gap-3">
                                        <button
                                            onClick={handleSaveMachineConfigs}
                                            disabled={savingConfigs}
                                            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            data-testid="save-machine-config-btn"
                                        >
                                            {savingConfigs ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                            Save Machine Configuration
                                        </button>
                                        <span className="text-sm text-gray-500">
                                            {enabledVariantUuids.size} of {machineConfigs.length} variants enabled
                                        </span>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
                </>
            )}

            {/* Save Button */}
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/edit-organization-info')}
                    className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors"
                >
                    Cancel
                </button>
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save Configuration
                </button>
            </div>

            {/* === Modals === */}

            {/* Category Create/Edit Modal */}
            {categoryModal && (
                <CategoryForm
                    title={categoryModal.type === 'create' ? 'Add Category' : 'Edit Category'}
                    initial={categoryModal.category ? {
                        name: categoryModal.category.name,
                        description: categoryModal.category.description,
                        imageUrl: categoryModal.category.imageUrl,
                        displayOrder: categoryModal.category.displayOrder,
                        active: categoryModal.category.active,
                    } : undefined}
                    onSubmit={async (data, imageFile) => {
                        if (categoryModal.type === 'create') {
                            await handleCreateCategory(data, imageFile);
                        } else if (categoryModal.category) {
                            await handleUpdateCategory(categoryModal.category.uuid, data, imageFile);
                        }
                    }}
                    onCancel={() => setCategoryModal(null)}
                    submitting={submitting}
                />
            )}

            {/* Variant Create/Edit Modal */}
            {variantModal && (
                <VariantForm
                    title={variantModal.type === 'create' ? 'Add Variant' : 'Edit Variant'}
                    initial={variantModal.variant ? {
                        sku: variantModal.variant.sku,
                        name: variantModal.variant.name,
                        priceInCents: variantModal.variant.priceInCents,
                        description: variantModal.variant.description,
                        displayOrder: variantModal.variant.displayOrder,
                        active: variantModal.variant.active,
                    } : undefined}
                    onSubmit={async (data) => {
                        if (variantModal.type === 'create') {
                            await handleCreateVariant(variantModal.categoryUuid, data);
                        } else if (variantModal.variant) {
                            await handleUpdateVariant(variantModal.variant.uuid, data);
                        }
                    }}
                    onCancel={() => setVariantModal(null)}
                    submitting={submitting}
                />
            )}

            {/* Delete Confirmation */}
            {deleteConfirm && (
                <ConfirmDialog
                    title={`Delete ${deleteConfirm.type === 'category' ? 'Category' : 'Variant'}`}
                    message={
                        deleteConfirm.type === 'category'
                            ? `Are you sure you want to delete "${deleteConfirm.name}"? This will also delete all its variants. This action cannot be undone.`
                            : `Are you sure you want to delete "${deleteConfirm.name}"? This action cannot be undone.`
                    }
                    onConfirm={() => {
                        if (deleteConfirm.type === 'category') {
                            handleDeleteCategory(deleteConfirm.uuid);
                        } else {
                            handleDeleteVariant(deleteConfirm.uuid);
                        }
                    }}
                    onCancel={() => setDeleteConfirm(null)}
                    confirming={submitting}
                />
            )}
        </div>
    );
};

export default VirtualProducts;
